import { useTheme } from '../context/ThemeContext';
import { Link } from 'react-router-dom';
import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';
import { trackPhoneConversion } from '../../../utils/analytics';

const steps = [
  {
    number: '01',
    title: 'Kennenlernen',
    desc: 'Ein Gespräch – über Ihr Geschäft, Ihre Ziele und was Ihre Website leisten soll.',
  },
  {
    number: '02',
    title: 'Gestalten & Texten',
    desc: 'Ihre Website wird individuell entworfen, getextet und bis ins Detail fertiggestellt.',
  },
  {
    number: '03',
    title: 'Live gehen',
    desc: 'Ihre Website geht online – und Sie fangen an, Anfragen zu bekommen.',
  },
];

export default function Support() {
  const { theme } = useTheme();
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: stepsRef, visibleItems } = useStaggerAnimation(3, { threshold: 0.1 });
  const { ref: imgRef, isVisible: imgVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: pointsRef, isVisible: pointsVisible } = useScrollAnimation({ threshold: 0.2 });

  const points = [
    { icon: 'ri-user-line', title: 'Direkter Draht', desc: 'Sie sprechen direkt mit dem, der mitdenkt und umsetzt.' },
    { icon: 'ri-message-3-line', title: 'Klare Ansagen', desc: 'Was geht. Was nicht geht. Ohne Umschweife.' },
    { icon: 'ri-search-eye-line', title: 'Ehrliche Einschätzung', desc: 'Keine Luftschlösser. Kein Sales-Gerede. Sondern eine klare Antwort, was sinnvoll ist.' },
  ];

  return (
    <section
      id="zusammenarbeit"
      className="py-12 sm:py-20 md:py-28 overflow-hidden"
      style={{ backgroundColor: '#111111', fontFamily: theme.fonts.body }}
    >
      <div className="max-w-7xl mx-auto px-4">

        {/* Process Steps – compact top strip */}
        <div
          ref={headRef}
          className="mb-10 sm:mb-16"
          style={{
            opacity: headVisible ? 1 : 0,
            transform: headVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <span className="text-xs tracking-[0.3em] uppercase font-semibold mb-4 block" style={{ color: theme.colors.accent }}>
            Zusammenarbeit
          </span>
          <h2
            className="text-3xl md:text-5xl font-black mb-10 leading-tight text-white"
            style={{ fontFamily: theme.fonts.heading }}
          >
            Kurze Wege.<br />Klare Worte.
          </h2>

          {/* Steps row */}
          <div ref={stepsRef} className="grid grid-cols-1 md:grid-cols-3 gap-0">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`flex items-start gap-5 py-5 sm:py-6 ${index > 0 ? 'md:pl-10' : ''} ${index < steps.length - 1 ? 'border-b md:border-b-0 md:border-r md:pr-10' : ''}`}
                style={{
                  borderColor: `${theme.colors.accent}18`,
                  opacity: visibleItems[index] ? 1 : 0,
                  transform: visibleItems[index] ? 'translateY(0)' : 'translateY(20px)',
                  transition: `opacity 0.6s ease ${index * 0.15}s, transform 0.6s ease ${index * 0.15}s`,
                }}
              >
                <span
                  className="text-3xl font-black leading-none shrink-0 w-11 pt-0.5"
                  style={{
                    fontFamily: theme.fonts.heading,
                    background: `linear-gradient(135deg, ${theme.colors.secondary}, ${theme.colors.accent})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {step.number}
                </span>
                <div>
                  <h3 className="text-base font-bold mb-1 text-white" style={{ fontFamily: theme.fonts.heading }}>
                    {step.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)' }}>
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div
            className="mt-8 sm:mt-10 h-px"
            style={{ background: `linear-gradient(90deg, transparent, ${theme.colors.accent}30, transparent)` }}
          />
        </div>

        {/* Main content: image + support points */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 lg:gap-16 items-center">

          {/* Left: Image */}
          <div
            ref={imgRef}
            className="relative h-[280px] sm:h-[360px] lg:h-[440px] rounded-3xl overflow-hidden order-2 lg:order-1"
            style={{
              opacity: imgVisible ? 1 : 0,
              transform: imgVisible ? 'translateX(0) scale(1)' : 'translateX(-40px) scale(0.95)',
              transition: 'opacity 0.9s ease, transform 0.9s ease',
            }}
          >
            <img
              src="https://readdy.ai/api/search-image?query=A%20sleek%20modern%20smartphone%20lying%20flat%20on%20a%20clean%20white%20marble%20desk%20surface%2C%20screen%20showing%20a%20beautifully%20designed%20website%20with%20clear%20typography%20and%20teal%20accents%2C%20a%20single%20white%20ceramic%20espresso%20cup%20placed%20beside%20it%2C%20soft%20diffused%20natural%20daylight%20from%20the%20side%20casting%20gentle%20shadows%2C%20no%20people%20or%20hands%20visible%20anywhere%2C%20minimal%20Scandinavian%20aesthetic%20with%20crisp%20white%20and%20warm%20neutral%20tones%2C%20close-up%20product%20photography%20style%20with%20shallow%20depth%20of%20field%20blurring%20the%20background%20softly&width=800&height=600&seq=support-phone-v1&orientation=landscape"
              alt="Persönlicher Support – Direkte Kommunikation"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
          </div>

          {/* Right: Support points */}
          <div className="order-1 lg:order-2">
            <p className="text-base mb-10 leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Kein Ticketsystem. Kein Callcenter.<br />
              <strong className="text-white">Sie haben eine Frage? Sie rufen an.</strong>
            </p>

            <div ref={pointsRef} className="space-y-6">
              {points.map((point, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 group"
                  style={{
                    opacity: pointsVisible ? 1 : 0,
                    transform: pointsVisible ? 'translateX(0)' : 'translateX(40px)',
                    transition: `opacity 0.6s ease ${i * 0.15}s, transform 0.6s ease ${i * 0.15}s`,
                  }}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
                    style={{ background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)` }}
                  >
                    <i className={`${point.icon} text-xl text-white`}></i>
                  </div>
                  <div>
                    <div className="font-bold text-base mb-1 text-white">{point.title}</div>
                    <div className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>{point.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-10">
              <a href="tel:+491607676763" onClick={(e) => { e.preventDefault(); trackPhoneConversion('tel:+491607676763'); }}>
                <button
                  className="px-8 py-3.5 font-bold text-sm rounded-xl transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer flex items-center gap-2"
                  style={{
                    background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)`,
                    color: '#FFFFFF',
                  }}
                >
                  <i className="ri-phone-line text-sm"></i>
                  Jetzt anrufen
                </button>
              </a>
              <Link to="/termin-buchen">
                <button
                  className="px-8 py-3.5 font-semibold text-sm rounded-xl transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
                  style={{
                    backgroundColor: 'transparent',
                    color: 'rgba(255,255,255,0.75)',
                    border: `2px solid ${theme.colors.accent}40`,
                  }}
                >
                  Online-Termin vereinbaren
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
